
import { Component } from 'vue-property-decorator';
import TreeMapFilterable from '@/components/reports-v3/components/filterables/TreeMapFilterable.vue';
import { TooltipFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tooltipFormatter';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import { PermissionsGroup, ValueText } from '@/store/models.def';
import { getTableName } from '@/worker/db/sqlHelpers';
import gdbx from '@/store/modules/gdbx';
import * as echarts from 'echarts';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		TreeMapFilterable,
		SelectFilter,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class SupplierAnalysisTreeMap extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['suppliers'];
	}

	public get tooltipFormatter() {
		return TooltipFormatter.basicTreeMapWithShare;
	}

	public get dataViewFormatter() {
		return DataViewFormatter.treemapWithShare;
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateRange', 'agents', 'suppliers'];

	public result: any = [];
	public series: echarts.ECharts[] | any = [];

	public selectedGrouping: any = null;
	public bigSupplierInput: number = 15000;
	public smallSupplierInput: number = 8000;
	public isCalculateGrouping: number = 0;

	public generateGrouping() {
		this.isCalculateGrouping += 1;
	}

	public get groupingList(): Array<ValueText<string>> {
		return [
			{ text: 'Supplier', value: 'supplier' },
			{ text: 'Stock Item', value: 'stockItems' },
			{ text: 'Stock Group', value: 'stockGroup' },
			{ text: 'Supplier Size', value: 'supplierSize' },
		];
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedAgents,
			selectedSuppliers,
			selectedGrouping,
			isCalculateGrouping,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAgents,
			selectedSuppliers,
			selectedGrouping,
			isCalculateGrouping,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const [dateStart, dateEnd] = this.selectedDateRange;
		const dateFilter = ` AND TRY_CAST("DOCDATE" AS BIGINT) >= ${dateStart} AND TRY_CAST("DOCDATE" AS BIGINT) <= ${dateEnd}`;

		let supplierFilter = '';
		if (this.selectedSuppliers.length > 0) {
			const list = this.selectedSuppliers.map((c) => `'${escapeStr(c)}'`).join(',');
			supplierFilter = ` AND "CODE" IN (${list})`;
		}
		let agentFilter = '';
		if (this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}

		const apDocTypes = this.includeDebitNote ? `'PI','SD'` : `'PI'`;
		const phDocTypes = this.includeDebitNote ? `'PI','CP','SD'` : `'PI','CP'`;

		const purchasesTable = getTableName('purchases');
		const purchaseReturnTable = getTableName('purchaseReturn');
		const tradesTable = getTableName('supplierTrades');

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded([
			'purchases', 'purchaseReturn', 'supplierTrades',
		] as any[]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		// === Summary data for supplier size defaults ===
		let purchaseAmount = 0;
		let purchaseCount = 0;
		let purchaseAmountMax = 0;

		if (this.slPhModuleOnly) {
			const [sumResult, cnResult] = await Promise.all([
				queryHead.rawQuery(`
					SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total,
						COUNT(*) as cnt,
						MAX(TRY_CAST("AMOUNT" AS DOUBLE)) as max_amt
					FROM ${purchasesTable}
					WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
						AND "DOCTYPE" IN (${phDocTypes})
				`),
				this.includeCreditNote
					? queryHead.rawQuery(`
						SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
						FROM ${purchaseReturnTable}
						WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
					`)
					: Promise.resolve([{ total: 0 }]),
			]);
			purchaseAmount = Number(sumResult[0] && sumResult[0].total) || 0;
			purchaseCount = Number(sumResult[0] && sumResult[0].cnt) || 0;
			purchaseAmountMax = Number(sumResult[0] && sumResult[0].max_amt) || 0;
			if (this.includeCreditNote) {
				purchaseAmount -= Number(cnResult[0] && cnResult[0].total) || 0;
			}
		} else {
			const [sumResult, cnResult] = await Promise.all([
				queryHead.rawQuery(`
					SELECT SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total,
						COUNT(*) as cnt,
						MAX(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as max_amt
					FROM ${tradesTable}
					WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
						AND "DOCTYPE" IN (${apDocTypes})
				`),
				this.includeCreditNote
					? queryHead.rawQuery(`
						SELECT SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total
						FROM ${tradesTable}
						WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
							AND "DOCTYPE" = 'SC'
					`)
					: Promise.resolve([{ total: 0 }]),
			]);
			purchaseAmount = Number(sumResult[0] && sumResult[0].total) || 0;
			purchaseCount = Number(sumResult[0] && sumResult[0].cnt) || 0;
			purchaseAmountMax = Number(sumResult[0] && sumResult[0].max_amt) || 0;
			if (this.includeCreditNote) {
				purchaseAmount -= Number(cnResult[0] && cnResult[0].total) || 0;
			}
		}
		this.checkInterrupted(jobId);

		if (this.isCalculateGrouping === 0) {
			const avgAmount = Math.round((purchaseAmount / purchaseCount) / 1000) * 1000;
			const halfMax = Math.round((purchaseAmountMax / 2) / 1000) * 1000;
			this.smallSupplierInput = Math.min(avgAmount, halfMax);
			this.bigSupplierInput = Math.max(avgAmount, halfMax);
		}

		this.generateLoadingText(0.5, 0.7);

		// === Grouping-specific queries ===
		let tradeDD: any[] = [];
		let tradeCNDD: any[] = [];
		let apDocumentAmount = 0;
		const isStockGrouping = this.selectedGrouping === 'stockItems'
			|| this.selectedGrouping === 'stockGroup';

		switch (this.selectedGrouping) {
			case 'stockGroup': {
				const [mainResult, cnResult] = await Promise.all([
					queryHead.rawQuery(`
						SELECT "STOCKGROUP" as grp, "ITEMCODE" as item,
							SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
						FROM ${purchasesTable}
						WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
							AND "DOCTYPE" IN (${phDocTypes})
						GROUP BY "STOCKGROUP", "ITEMCODE"
					`),
					this.includeCreditNote
						? queryHead.rawQuery(`
							SELECT "STOCKGROUP" as grp, "ITEMCODE" as item,
								SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
							FROM ${purchaseReturnTable}
							WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
							GROUP BY "STOCKGROUP", "ITEMCODE"
						`)
						: Promise.resolve([]),
				]);

				const groupMap = new Map<string, Map<string, number>>();
				for (const row of mainResult) {
					const grp = String(row.grp || '');
					const item = String(row.item || '');
					if (!groupMap.has(grp)) { groupMap.set(grp, new Map()); }
					const items = groupMap.get(grp)!;
					items.set(item, (items.get(item) || 0) + (Number(row.total) || 0));
				}

				const cnGroupMap = new Map<string, Map<string, number>>();
				for (const row of cnResult) {
					const grp = String(row.grp || '');
					const item = String(row.item || '');
					if (!cnGroupMap.has(grp)) { cnGroupMap.set(grp, new Map()); }
					const items = cnGroupMap.get(grp)!;
					items.set(item, (items.get(item) || 0) + (Number(row.total) || 0));
				}

				for (const [grp, items] of groupMap) {
					let grpSum = 0;
					const subDimension: any[] = [];
					for (const [item, amount] of items) {
						grpSum += amount;
						subDimension.push({
							text: gdbx.stockItemNames[item] || item,
							value: [item], sum: amount,
						});
					}
					tradeDD.push({
						text: gdbx.stockGroupNames[grp] || grp,
						value: [grp], sum: grpSum, subDimension,
					});
				}

				for (const [grp, items] of cnGroupMap) {
					let grpSum = 0;
					const subDimension: any[] = [];
					for (const [item, amount] of items) {
						grpSum += amount;
						subDimension.push({
							text: gdbx.stockItemNames[item] || item,
							value: [item], sum: amount,
						});
					}
					tradeCNDD.push({
						text: gdbx.stockGroupNames[grp] || grp,
						value: [grp], sum: grpSum, subDimension,
					});
				}
				break;
			}
			case 'stockItems': {
				const [mainResult, cnResult] = await Promise.all([
					queryHead.rawQuery(`
						SELECT "ITEMCODE" as code,
							SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
						FROM ${purchasesTable}
						WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
							AND "DOCTYPE" IN (${phDocTypes})
						GROUP BY "ITEMCODE"
					`),
					this.includeCreditNote
						? queryHead.rawQuery(`
							SELECT "ITEMCODE" as code,
								SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
							FROM ${purchaseReturnTable}
							WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
							GROUP BY "ITEMCODE"
						`)
						: Promise.resolve([]),
				]);

				tradeDD = mainResult.map((row: any) => ({
					text: gdbx.stockItemNames[String(row.code)] || String(row.code),
					value: [String(row.code || '')],
					sum: Number(row.total) || 0,
				}));

				const cnMap = new Map<string, number>();
				for (const row of cnResult) {
					cnMap.set(String(row.code || ''), Number(row.total) || 0);
				}
				tradeCNDD = tradeDD.map((dd: any) => ({
					text: dd.text, value: dd.value,
					sum: cnMap.get(dd.value[0]) || 0,
				}));
				break;
			}
			default: {
				// Supplier or supplierSize - GROUP BY supplier code
				if (this.slPhModuleOnly) {
					const [mainResult, cnResult] = await Promise.all([
						queryHead.rawQuery(`
							SELECT "CODE" as code,
								SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
							FROM ${purchasesTable}
							WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
								AND "DOCTYPE" IN (${phDocTypes})
							GROUP BY "CODE"
						`),
						this.includeCreditNote
							? queryHead.rawQuery(`
								SELECT "CODE" as code,
									SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
								FROM ${purchaseReturnTable}
								WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
								GROUP BY "CODE"
							`)
							: Promise.resolve([]),
					]);

					tradeDD = mainResult.map((row: any) => ({
						text: gdbx.supplierNames[String(row.code)] || String(row.code),
						value: [String(row.code || '')],
						sum: Number(row.total) || 0,
					}));

					const cnMap = new Map<string, number>();
					for (const row of cnResult) {
						cnMap.set(String(row.code || ''), Number(row.total) || 0);
					}
					tradeCNDD = tradeDD.map((dd: any) => ({
						text: dd.text, value: dd.value,
						sum: cnMap.get(dd.value[0]) || 0,
					}));
				} else {
					const [mainResult, cnResult] = await Promise.all([
						queryHead.rawQuery(`
							SELECT "CODE" as code,
								SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total
							FROM ${tradesTable}
							WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
								AND "DOCTYPE" IN (${apDocTypes})
							GROUP BY "CODE"
						`),
						this.includeCreditNote
							? queryHead.rawQuery(`
								SELECT "CODE" as code,
									SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total
								FROM ${tradesTable}
								WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
									AND "DOCTYPE" = 'SC'
								GROUP BY "CODE"
							`)
							: Promise.resolve([]),
					]);

					tradeDD = mainResult.map((row: any) => ({
						text: gdbx.supplierNames[String(row.code)] || String(row.code),
						value: [String(row.code || '')],
						sum: Number(row.total) || 0,
					}));

					const cnMap = new Map<string, number>();
					for (const row of cnResult) {
						cnMap.set(String(row.code || ''), Number(row.total) || 0);
					}
					tradeCNDD = tradeDD.map((dd: any) => ({
						text: dd.text, value: dd.value,
						sum: cnMap.get(dd.value[0]) || 0,
					}));
				}
			}
		}
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.7, 0.9);

		// AP Document amount for stock groupings in AP mode
		if (!this.slPhModuleOnly && isStockGrouping) {
			const [apResult, apCnResult, phResult, phCnResult] = await Promise.all([
				queryHead.rawQuery(`
					SELECT SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total
					FROM ${tradesTable}
					WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
						AND "DOCTYPE" IN (${apDocTypes})
				`),
				this.includeCreditNote
					? queryHead.rawQuery(`
						SELECT SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total
						FROM ${tradesTable}
						WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
							AND "DOCTYPE" = 'SC'
					`)
					: Promise.resolve([{ total: 0 }]),
				queryHead.rawQuery(`
					SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
					FROM ${purchasesTable}
					WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
						AND "DOCTYPE" IN (${phDocTypes})
				`),
				this.includeCreditNote
					? queryHead.rawQuery(`
						SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
						FROM ${purchaseReturnTable}
						WHERE 1=1${dateFilter}${supplierFilter}${agentFilter}
					`)
					: Promise.resolve([{ total: 0 }]),
			]);

			const apTotal = Number(apResult[0] && apResult[0].total) || 0;
			const apCn = Number(apCnResult[0] && apCnResult[0].total) || 0;
			const phTotal = Number(phResult[0] && phResult[0].total) || 0;
			const phCn = Number(phCnResult[0] && phCnResult[0].total) || 0;
			apDocumentAmount = (apTotal - apCn) - (phTotal - phCn);
		}
		this.checkInterrupted(jobId);

		// === Build treemap result ===
		this.generateLoadingText(1);
		this.result = [];

		const treeColors = [
			'#8975ea', '#acbeff', '#accdff', '#acdaff', '#cdc8f9', '#c7b5e2',
		];

		if (this.selectedGrouping === 'supplierSize') {
			const getCnForSupplier = (supplierCode: any) => {
				if (!this.includeCreditNote) { return 0; }
				const cnRecord = tradeCNDD.find((cn: any) => cn.value[0] === supplierCode);
				return cnRecord ? cnRecord.sum : 0;
			};

			// Above threshold
			const aboveSuppliers = tradeDD.filter((dd: any) => dd.sum > this.bigSupplierInput);
			const aboveChildren = aboveSuppliers.map((supplier: any) => ({
				name: supplier.text,
				value: supplier.sum - getCnForSupplier(supplier.value[0]),
				color: treeColors,
				colorSaturation: [0.9, 0.4],
			}));
			this.result.push({
				name: 'Above ' + String(this.bigSupplierInput),
				value: aboveChildren.map((c: any) => c.value).reduce((a: number, b: number) => a + b, 0),
				color: treeColors,
				colorSaturation: [0.9, 0.4],
				children: aboveChildren,
			});

			// Between thresholds
			const betweenSuppliers = tradeDD.filter(
				(dd: any) =>
					dd.sum >= this.smallSupplierInput &&
					dd.sum <= this.bigSupplierInput,
			);
			const betweenChildren = betweenSuppliers.map((supplier: any) => ({
				name: supplier.text,
				value: supplier.sum - getCnForSupplier(supplier.value[0]),
				color: treeColors,
				colorSaturation: [0.9, 0.4],
			}));
			this.result.push({
				name:
					'Between ' +
					String(this.smallSupplierInput) +
					' and ' +
					String(this.bigSupplierInput),
				value: betweenChildren.map((c: any) => c.value).reduce((a: number, b: number) => a + b, 0),
				color: treeColors,
				colorSaturation: [0.9, 0.4],
				children: betweenChildren,
			});

			// Below threshold
			const belowSuppliers = tradeDD.filter((dd: any) => dd.sum < this.smallSupplierInput);
			const belowChildren = belowSuppliers.map((supplier: any) => ({
				name: supplier.text,
				value: supplier.sum - getCnForSupplier(supplier.value[0]),
				color: treeColors,
				colorSaturation: [0.9, 0.4],
			}));
			this.result.push({
				name: 'Below ' + String(this.smallSupplierInput),
				value: belowChildren.map((c: any) => c.value).reduce((a: number, b: number) => a + b, 0),
				color: treeColors,
				colorSaturation: [0.9, 0.4],
				children: belowChildren,
			});
		} else {
			// Build CN lookup by text for Map-based matching
			const cnLookup = new Map<string, any>();
			for (const cn of tradeCNDD) {
				cnLookup.set(cn.text, cn);
			}

			tradeDD.forEach((trade: any) => {
				const cn = this.includeCreditNote ? cnLookup.get(trade.text) : null;
				const cnSum = cn ? cn.sum : 0;
				const subDD = trade.subDimension;

				// Build CN sub-item lookup
				const cnSubMap = cn && cn.subDimension
					? new Map(cn.subDimension.map((s: any) => [s.text, s.sum]))
					: null;

				this.result.push({
					name: trade.text,
					value: trade.sum - cnSum,
					color: treeColors,
					colorSaturation: [0.9, 0.4],
					children: subDD
						? subDD.map((subdd: any) => ({
							name: subdd.text,
							value: subdd.sum - (cnSubMap ? Number(cnSubMap.get(subdd.text)) || 0 : 0),
							color: treeColors,
							colorSaturation: [0.9, 0.4],
						}))
						: [],
				});
			});

			// Add AP Document group for stock groupings in AP mode
			if (!this.slPhModuleOnly && isStockGrouping && apDocumentAmount !== 0) {
				this.result.push({
					name: 'Supplier Module Documents',
					value: apDocumentAmount,
					color: [
						'#f5a623', '#f8c471', '#fad7a0', '#fdebd0', '#fef9e7', '#fff5eb',
					],
					colorSaturation: [0.9, 0.4],
					children: [],
				});
			}
		}

		this.result = this.result.filter((item: any) => item.value > 0);

		this.series = [{
			name: 'Supplier Analysis',
			type: 'treemap',
			roam: 'move',
			width: '100%',
			drillDownIcon: '',
			label: { color: '#000' },
			itemStyle: { gapWidth: 1, borderWidth: 1 },
			levels: [
				{
					itemStyle: { borderWidth: 2, gapWidth: 2 },
					upperLabel: { show: false, fontWeight: 'bold', color: '#000' },
					emphasis: {
						itemStyle: { borderWidth: 0, gapWidth: 2 },
						upperLabel: { show: true, fontWeight: 'bold', color: '#000' },
					},
				},
				{
					colorSaturation: [0.25, 0.6],
					itemStyle: { borderColorSaturation: 0.7, gapWidth: 2, borderWidth: 2 },
					upperLabel: { show: true, color: '#000', fontWeight: 'bold' },
					emphasis: {
						itemStyle: { borderWidth: 0, gapWidth: 1 },
						upperLabel: { show: true, color: '#000', fontWeight: 'bold' },
					},
				},
				{
					itemStyle: { gapWidth: 1, borderWidth: 1 },
					upperLabel: { show: true, color: '#000', fontWeight: 'bold' },
					emphasis: {
						itemStyle: { borderWidth: 0, gapWidth: 1 },
						upperLabel: { show: true, color: '#000', fontWeight: 'bold' },
					},
				},
			],
			data: this.result,
		}];

		this.saveHistory(
			'result',
			'series',
			'bigSupplierInput',
			'smallSupplierInput',
		);
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
